import { Icon } from "@studio3t/generic-components";
import type { JobMetadata, Service } from "@/types/api";
import { assertNever } from "@/utils/assertNever";

export function getProviderLabel(provider: Service["provider"]): string {
  switch (provider) {
    case "mongodb":
      return "MongoDB";
    case "postgres":
      return "PostgreSQL";
    case "postgres_cdc":
      return "PostgreSQL CDC";
    case "pubsub":
      return "Google Pub/Sub";
    case "kafka":
      return "Kafka";
    case "udf":
      return "Scripts (UDF)";
    case "http":
      return "HTTP Client";
    case "http_source":
      return "HTTP Source";
    case "generator":
      return "Generator";
    case "scanner":
      return "Scanner";
    case "anthropic":
      return "Anthropic";
    case "bedrock":
      return "AWS Bedrock";
    case "openai":
      return "OpenAI";
    default:
      // Not `return provider`: a provider with no case rendered its raw name
      // to the user and nothing noticed. This makes it a build error.
      return assertNever(provider);
  }
}

export function getProviderIcon(provider: Service["provider"]): Icon {
  switch (provider) {
    case "mongodb":
    case "postgres":
    case "postgres_cdc":
      return Icon.Database;
    case "pubsub":
      return Icon.Notification;
    case "kafka":
      return Icon.Lightning;
    case "udf":
      // A UDF service is its scripts folder (Figma 14058-10184).
      return Icon.Folder;
    case "http":
      return Icon.Link;
    case "http_source":
      return Icon.Connection;
    case "generator":
      return Icon.Lightning;
    case "scanner":
      return Icon.Database;
    case "anthropic":
    case "bedrock":
    case "openai":
      return Icon.AI;
    default:
      return assertNever(provider);
  }
}

export function maskConnectionString(connString?: string): string {
  if (!connString) return "—";
  try {
    const url = new URL(connString);
    if (url.password) {
      url.password = "****";
    }
    return url.toString();
  } catch {
    if (connString.length > 50) {
      return `${connString.substring(0, 50)}...`;
    }
    return connString;
  }
}

export function buildJobStatusMap(
  jobs: JobMetadata[] | undefined,
): Record<string, boolean> {
  if (!jobs) return {};
  const map: Record<string, boolean> = {};
  for (const job of jobs) {
    map[job.name] = job.state.toLowerCase() === "running";
  }
  return map;
}

export function findJobsUsingService(
  jobs: JobMetadata[] | undefined,
  serviceName: string | undefined,
  service: Service | undefined,
  jobStatusMap: Record<string, boolean>,
): { name: string; running: boolean }[] {
  if (!jobs || !serviceName) return [];
  const jobNames = new Set<string>();

  if (service?.used_by_jobs) {
    for (const j of service.used_by_jobs) {
      jobNames.add(j);
    }
  }

  for (const job of jobs) {
    const pipeline = job.pipeline;
    if (!pipeline) continue;

    if (pipeline.source.service_name === serviceName) {
      jobNames.add(job.name);
    }
    for (const mw of pipeline.middlewares || []) {
      if (mw.service_name === serviceName) {
        jobNames.add(job.name);
      }
    }
    for (const sink of pipeline.sinks) {
      if (sink.service_name === serviceName) {
        jobNames.add(job.name);
      }
    }
  }

  return Array.from(jobNames).map((jobName) => ({
    name: jobName,
    running: jobStatusMap[jobName] ?? false,
  }));
}
